import {atom, useAtom} from 'jotai';
import {CouponProps} from 'modules/coupons/types';
import {
  GuestUserProps,
  OrderAddressProps,
  OrderTypeEnum,
} from 'modules/orders/types';
import useProductsByIds from 'modules/products/hooks/use-products-by-ids';
import {
  AdditionalGroupConfigTypeEnum,
  ProductProps,
} from 'modules/products/types';
import roundTo from 'shared/util/round-to';
import {CartPaymentProps, CartProductProps, CartProps} from '../types';
import useCompanyDetails from 'modules/companies/hooks/use-get-company-details';
import useAccessControls from 'modules/access-controls/atoms/use-access-controls';
import useDeliveryPrice from 'modules/orders/hooks/use-delivery-price';
import {useDebounce} from 'use-debounce';
import useUserByPhone from 'modules/users/hooks/use-user-by-phone';
import {useEffect} from 'react';
import useUser from 'modules/users/hooks/use-user';

const cartAtom = atom<CartProps>({products: [], type: OrderTypeEnum.delivery});

const useCart = () => {
  const [cart, setCart] = useAtom(cartAtom);
  const {data = [], isLoading} = useProductsByIds(
    cart?.products.map((product) => product.id) ?? [],
  );

  const accessControls = useAccessControls();

  const companyId = cart?.companyId ?? accessControls?.companyId;

  const getAdditionalsPrice = (
    cartProduct: CartProductProps,
    product: ProductProps,
  ) => {
    if (!cartProduct.additionalGroups) return 0;

    const additionalsPrice = Object.keys(cartProduct.additionalGroups).reduce(
      (total, additionalGroupCartId) => {
        const additionalGroupConfig = product.additionalGroupsConfig.find(
          (additionalGroupConfig) =>
            additionalGroupConfig.id === additionalGroupCartId,
        );

        if (!additionalGroupConfig) return total;

        const {isFree, additionalPricesWhenFree} = additionalGroupConfig;

        const additionals =
          cartProduct.additionalGroups![additionalGroupCartId];

        let additionalsPrice = additionals.reduce((total, {id, quantity}) => {
          const additional =
            additionalGroupConfig.additionalGroup.additionals.find(
              (someAdditional) => someAdditional.id === id,
            );

          if (!additional) return total;

          const chargeWhenFreePrice =
            additionalPricesWhenFree?.find((additional) => additional.id === id)
              ?.price ?? 0;

          const price = !isFree ? additional.price : chargeWhenFreePrice;

          return total + price * quantity;
        }, 0);

        if (
          additionalGroupConfig.type ===
          AdditionalGroupConfigTypeEnum.highest_value
        )
          additionalsPrice = additionals.reduce((highestValue, {id}) => {
            const additional =
              additionalGroupConfig.additionalGroup.additionals.find(
                (someAdditional) => someAdditional.id === id,
              );

            if (!additional) return highestValue;

            const chargeWhenFreePrice =
              additionalPricesWhenFree?.find(
                (additional) => additional.id === id,
              )?.price ?? 0;

            const price = !isFree ? additional.price : chargeWhenFreePrice;

            return price > highestValue ? price : highestValue;
          }, 0);

        if (
          additionalGroupConfig.type === AdditionalGroupConfigTypeEnum.average
        ) {
          const quantity = additionals.reduce(
            (total, cartAdditional) => total + cartAdditional.quantity,
            0,
          );
          additionalsPrice = additionalsPrice / quantity;
        }

        return additionalsPrice + total;
      },
      0,
    );

    return additionalsPrice;
  };

  const getProductPrice = (cartProduct: CartProductProps) => {
    const product = data.find(
      (someProduct) => someProduct.id === cartProduct.id,
    );
    if (!product) return 0;

    const size = product.sizes.find(
      (size) => size.name === cartProduct.size?.name,
    );

    const productPrice =
      product.priceMode === 'fixed'
        ? cartProduct.saleId
          ? product.salePrice ?? product.price
          : product.price
        : (cartProduct.saleId ? size?.salePrice ?? size?.price : size?.price) ??
          0;

    return (
      (productPrice + getAdditionalsPrice(cartProduct, product)) *
      cartProduct.quantity
    );
  };

  const total = cart?.products.reduce(
    (total, cartProduct) => total + roundTo(getProductPrice(cartProduct)),
    0,
  );

  const addProduct = (companyId: string, product: CartProductProps) => {
    if (cart?.companyId && cart.companyId !== companyId)
      return setCart({
        type: OrderTypeEnum.delivery,
        products: [product],
        companyId,
      });

    setCart((cart) =>
      cart
        ? {...cart, products: [...(cart?.products ?? []), product]}
        : {type: OrderTypeEnum.delivery, products: [product], companyId},
    );
  };

  const updateProduct = (product: CartProductProps, index: number) =>
    setCart((cart) => {
      const products = cart!.products;
      products[index] = product;

      return {...cart, products};
    });

  const addPayment = (payment: CartPaymentProps) =>
    setCart((cart) => cart && {...cart, payment});

  const clearCart = () => setCart({products: []});

  const addDocument = (document: string) => {
    setCart((cart) => cart && {...cart, document});
  };

  const removeDocument = () => {
    setCart((cart) => cart && {...cart, document: undefined});
  };

  const addBalance = (balance: number) => {
    setCart((cart) => cart && {...cart, balance});
  };

  const removeBalance = () => {
    setCart((cart) => cart && {...cart, balance: undefined});
  };

  const changeOrderType = () => {
    setCart(
      (cart) =>
        cart && {
          ...cart,
          type:
            cart.type === OrderTypeEnum.delivery
              ? OrderTypeEnum.pickup
              : OrderTypeEnum.delivery,
        },
    );
  };

  const addCoupon = (coupon: CouponProps) => {
    setCart((cart) => cart && {...cart, coupon});
  };

  const setGuestUser = (guestUser: GuestUserProps) => {
    setCart((cart) => cart && {...cart, guestUser});
  };

  const removeCoupon = () => {
    setCart((cart) => cart && {...cart, coupon: undefined});
  };

  const removeProduct = (index: number) => {
    if (cart?.products.length === 1) {
      setCart({products: []});
      return;
    }

    setCart(
      (cart) =>
        cart && {
          ...cart,
          products: cart.products.filter(
            (_, productIndex) => productIndex !== index,
          ),
        },
    );
  };

  const incrementQuantity = (index: number) => {
    setCart(
      (cart) =>
        cart && {
          ...cart,
          products: cart.products.map((someProduct, productIndex) =>
            productIndex === index
              ? {...someProduct, quantity: someProduct.quantity + 1}
              : someProduct,
          ),
        },
    );
  };

  const decrementQuantity = (index: number) => {
    const shouldRemoveProduct = cart?.products.find(
      (someProduct, productIndex) =>
        productIndex === index && someProduct.quantity === 1,
    );

    if (shouldRemoveProduct) return removeProduct(index);

    setCart(
      (cart) =>
        cart && {
          ...cart,
          products: cart.products.map((someProduct, productIndex) =>
            productIndex === index
              ? {...someProduct, quantity: someProduct.quantity - 1}
              : someProduct,
          ),
        },
    );
  };

  const setObservation = (observation: string) => {
    setCart((cart) => cart && {...cart, observation});
  };

  const setRetainedEarnings = (retainedEarnings: boolean) => {
    setCart((cart) => cart && {...cart, retainedEarnings});
  };

  const setAddress = (address: OrderAddressProps) => {
    setCart((cart) => cart && {...cart, address});
  };

  const removeAddress = () => {
    setCart((cart) => cart && {...cart, address: undefined});
  };

  const discount =
    !cart || !cart.coupon
      ? 0
      : cart.coupon.type === 'value'
      ? cart.coupon.value
      : (total ?? 0) * (cart.coupon.value / 100);

  const deliveryPriceQuery = useDeliveryPrice({
    companyId: cart?.companyId ?? companyId!,
    latitude: cart?.address?.latitude,
    longitude: cart?.address?.longitude,
    neighborhoodId: cart?.address?.neighborhoodId,
  });

  const deliveryPrice =
    cart?.type === OrderTypeEnum.delivery
      ? deliveryPriceQuery.data?.deliveryPrice ?? 0
      : 0;

  const companyQuery = useCompanyDetails(companyId ?? undefined);

  const freeDeliveryThreshold =
    companyQuery.data?.freeDeliveryThreshold &&
    total &&
    total >= companyQuery.data.freeDeliveryThreshold;

  const isSale = cart?.products.some(
    (product) => product.saleId || product.size?.salePrice,
  );

  const [debouncedPhone] = useDebounce(cart?.guestUser?.phone, 1000);
  const userByPhoneQuery = useUserByPhone(
    !cart?.userId ? debouncedPhone : undefined,
  );

  const userQuery = useUser(cart?.userId);

  const guestUser = cart?.guestUser ?? ({} as GuestUserProps);

  useEffect(() => {
    const query = userByPhoneQuery.data ?? userQuery.data;
    if (query && query.name !== guestUser?.name) {
      const {name, email, cpf, phone} = query;

      setGuestUser({
        ...guestUser,
        name,
        email,
        document: cpf,
        phone: phone!,
      });
    }
  }, [userByPhoneQuery, userQuery, setGuestUser]);

  return {
    cart,
    addProduct,
    removeDocument,
    updateProduct,
    addPayment,
    clearCart,
    addDocument,
    changeOrderType,
    addCoupon,
    removeCoupon,
    removeProduct,
    addBalance,
    removeBalance,
    total: total!,
    isLoading,
    incrementQuantity,
    decrementQuantity,
    setObservation,
    discount,
    deliveryPrice,
    companyQuery,
    deliveryPriceQuery,
    setGuestUser,
    freeDeliveryThreshold,
    isSale,
    setAddress,
    removeAddress,
    guestUser,
    userByPhoneQuery,
    setRetainedEarnings,
    userQuery,
    setCart,
  };
};

export default useCart;
